import { getATLContextUrl } from '@atlaskit/atlassian-context/get-atl-context-url';
import type { FireEventType } from '@atlaskit/teams-app-internal-analytics/types';

import {
	type AgentIdType,
	type AgentPermissions,
	type ClientOverrides,
	type ProfileClient,
	type ProfileClientOptions,
	type RovoAgentCardClientResult,
	type Team,
	type TeamCentralReportingLinesData,
} from '../types';

import { defaultConfig } from './defaultConfig';
import { maybeCreateTeamCentralClient } from './maybeCreateTeamCentralClient';
import { default as RovoAgentCardClient } from './RovoAgentCardClient';
import { type default as TeamCentralCardClient } from './TeamCentralCardClient';
import TeamProfileCardClient from './TeamProfileCardClient';
import UserProfileCardClient from './UserProfileCardClient';

export type TeamCentralScopes = { withOrgContext: true; withSiteContext: boolean };

export class ProfileCardClient implements ProfileClient {
	userClient: UserProfileCardClient;
	teamClient: TeamProfileCardClient;
	tcClient?: TeamCentralCardClient;
	rovoAgentClient: RovoAgentCardClient;

	constructor(config: ProfileClientOptions, clients?: ClientOverrides) {
		//This default can be removed once all the clients are updated to pass the gatewayGraphqlUrl
		const withDefaultConfig = {
			...defaultConfig,
			...config,
			...{ gatewayGraphqlUrl: config.gatewayGraphqlUrl ?? defaultConfig.gatewayGraphqlUrl },
		};
		this.userClient = clients?.userClient || new UserProfileCardClient(withDefaultConfig);
		this.teamClient = clients?.teamClient || new TeamProfileCardClient(withDefaultConfig);
		this.rovoAgentClient = clients?.rovoAgentClient || new RovoAgentCardClient(withDefaultConfig);
		this.tcClient = maybeCreateTeamCentralClient(withDefaultConfig, clients);
	}

	flushCache(): void {
		this.userClient.flushCache();
		this.teamClient.flushCache();
		this.tcClient?.flushCache();
		this.rovoAgentClient?.flushCache();
	}

	getProfile(cloudId: string, userId: string, analytics?: FireEventType): Promise<any> {
		return this.userClient.getProfile(cloudId, userId, analytics);
	}

	getTeamProfile(teamId: string, orgId?: string, analytics?: FireEventType): Promise<Team> {
		return this.teamClient.getProfile(teamId, orgId, analytics);
	}

	getReportingLines(userId: string): Promise<TeamCentralReportingLinesData> {
		return (
			this.tcClient?.getReportingLines(userId) || Promise.resolve({ managers: [], reports: [] })
		);
	}

	async getTeamCentralBaseUrl(teamCentralScopes?: TeamCentralScopes): Promise<string | undefined> {
		if (this.tcClient === undefined) {
			return Promise.resolve(undefined);
		}

		const isGlobalExperienceWorkspace = await this.tcClient.getIsGlobalExperienceWorkspace();

		if (!isGlobalExperienceWorkspace) {
			return Promise.resolve(getATLContextUrl('team'));
		}

		let suffix = '';
		if (teamCentralScopes !== undefined) {
			const orgId = await this.tcClient.getOrgId();
			if (orgId === null) {
				return Promise.resolve(undefined);
			}

			suffix += `/o/${orgId}`;

			if (teamCentralScopes.withSiteContext) {
				suffix += `/s/${this.tcClient.options.cloudId}`;
			}
		}

		return Promise.resolve(`${getATLContextUrl('home')}${suffix}`);
	}

	async shouldShowGiveKudos(): Promise<boolean> {
		if (!this.tcClient || !(await this.getTeamCentralBaseUrl())) {
			return Promise.resolve(false);
		}
		return this.tcClient.checkWorkspaceExists();
	}

	getRovoAgentProfile(
		id: AgentIdType,
		analytics?: FireEventType,
	): Promise<RovoAgentCardClientResult> {
		return this.rovoAgentClient?.getProfile(id, analytics);
	}

	getRovoAgentPermissions(id: string, fireAnalytics?: FireEventType): Promise<AgentPermissions> {
		return this.rovoAgentClient?.getPermissions(id, fireAnalytics);
	}

	deleteAgent(id: string, analytics?: FireEventType): Promise<void> {
		return this.rovoAgentClient?.deleteAgent(id, analytics);
	}

	setFavouriteAgent(id: string, isFavourite: boolean, analytics?: FireEventType): Promise<void> {
		return this.rovoAgentClient?.setFavouriteAgent(id, isFavourite, analytics);
	}
}

/**
 * @deprecated Use the `ProfileCardClient` export from
 * `@atlaskit/profilecard/profile-card-client` instead.
 */
export { ProfileCardClient as ProfileClient };

export default ProfileCardClient;
